import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, LoadingSpinner, InlineAlert } from '../components/BaseComponents';
import CanonicalModal from '../components/BaseComponents/CanonicalModal/CanonicalModal';
import usePublicApi from '../hooks/usePublicApi';
import useApi from '../hooks/useApi';
import { userContext } from '../context/UserContext';
import { TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus';
import { getApiErrorMessage, getCSRFToken } from '../lib/utils';
import routes from '../components/Router/routes';

type PreviewInfo = {
    isOpen: boolean;
}

/**
 * Sub-task C accept-invite flow (docs/todo/user-org-membership-c-existing-user-invitations.md) -
 * reads `?inviteToken=` from the query string (reached from InviteSignUp.tsx's ACCOUNT_EXISTS
 * redirect, Sub-task I item A.1) and either prompts a login (preserving a redirect back here) or,
 * once logged in, lets the existing account accept the invitation and join the organisation.
 */
function AcceptInvite() {
    const location = useLocation();
    const navigate = useNavigate();
    const { userData, fetchUserData } = useContext(userContext);
    const publicApi = usePublicApi();
    const api = useApi();

    const inviteToken = new URLSearchParams(location.search).get('inviteToken') ?? '';

    const [loading, setLoading] = useState(true);
    const [previewInfo, setPreviewInfo] = useState<PreviewInfo | null>(null);
    const [companyName, setCompanyName] = useState('');
    const [notFound, setNotFound] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [confirmingDecline, setConfirmingDecline] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!inviteToken) {
            setNotFound(true);
            setLoading(false);
            return;
        }
        let cancelled = false;
        const run = async () => {
            try {
                const result = await publicApi.get(`/invitations/${inviteToken}/preview`);
                // The name is only returned to the invited account itself; anyone else sees "not found".
                if (userData && result.data?.isOpen) {
                    const details = await api.get(`/invitations/${inviteToken}/details`);
                    if (!cancelled) {
                        setCompanyName(details.data.companyName);
                    }
                }
                if (!cancelled) {
                    setPreviewInfo(result.data);
                }
            } catch (err) {
                if (!cancelled) {
                    setNotFound(true);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }
        run();
        return () => { cancelled = true; }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteToken, !!userData]);

    const onAccept = async () => {
        setSubmitting(true);
        setError('');
        try {
            await api.post(`/invitations/${inviteToken}/accept`, {
                _csrf: await getCSRFToken(),
            });
            // Refresh the session so the new membership is reflected before landing.
            await fetchUserData();
            navigate(routes.landingPage.path, {
                replace: true,
                state: { message: 'You have joined the organisation.' },
            });
        } catch (err: any) {
            setSubmitting(false);
            setError(getApiErrorMessage(err, 'Failed to accept this invitation. Please try again.'));
        }
    }

    const onDecline = async () => {
        setSubmitting(true);
        setError('');
        try {
            await api.post(`/invitations/${inviteToken}/decline`, {
                _csrf: await getCSRFToken(),
            });
            setConfirmingDecline(false);
            navigate(routes.landingPage.path, {
                replace: true,
                state: { message: 'You have declined the invitation.' },
            });
        } catch (err: any) {
            setSubmitting(false);
            setConfirmingDecline(false);
            setError(getApiErrorMessage(err, 'Failed to decline this invitation. Please try again.'));
        }
    }

    if (loading) {
        return <LoadingSpinner />
    }

    if (notFound || !previewInfo || !previewInfo.isOpen) {
        return (
            <div className="content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16">
                <div className="text-charcoal-100 text-2xl text-center">Invitation not found</div>
                <div className="text-charcoal-100 text-center mt-4">
                    This invitation link is invalid or has expired.
                    <br />
                    Please contact whoever invited you for a new link.
                    {userData && (
                        <>
                            <br />
                            Make sure you are logged in with the invited address.
                        </>
                    )}
                </div>
                <div className="mt-6 text-center">
                    <Link className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} to={routes.publicLogin.path}>Log in</Link>
                </div>
            </div>
        )
    }

    if (!userData) {
        return (
            <div className="content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16">
                <div className="text-charcoal-100 text-2xl text-center">Welcome</div>
                <div className="text-charcoal-100 text-center mt-4">
                    Log in to accept the invitation.
                </div>
                <div className="mt-6 text-center">
                    <Link
                        className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`}
                        to={routes.publicLogin.path}
                        state={{ redirectTo: `${location.pathname}${location.search}` }}
                    >
                        Log in
                    </Link>
                </div>
            </div>
        )
    }

    return (
        <div className="content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16 mb-8">
            <div className="text-charcoal-100 text-2xl text-center">Welcome</div>
            <div className="text-charcoal-100 text-center mt-4">
                You have been invited to join <b>{companyName}</b> on Neoni.
            </div>
            { error && (
                <InlineAlert type='error' className='mt-6'>{ error }</InlineAlert>
            )}
            <div className='mt-6 flex justify-start'>
                <Button
                    disabled={submitting}
                    type="Primary"
                    text="Accept invitation"
                    context="form-field"
                    onClick={onAccept}
                />
                <Button
                    disabled={submitting}
                    type="Outlined"
                    text="Decline invitation"
                    context="form-field"
                    additionalClass="ml-3"
                    onClick={() => setConfirmingDecline(true)}
                />
            </div>
            <CanonicalModal
                open={confirmingDecline}
                onClose={() => setConfirmingDecline(false)}
                size='small'
                title='Decline invitation'
                actions={
                    <div className='flex space-x-3'>
                        <Button type='Primary' text='Confirm decline' disabled={submitting} onClick={onDecline} />
                        <Button type='Outlined' text='Cancel' disabled={submitting} onClick={() => setConfirmingDecline(false)} />
                    </div>
                }
            >
                <div className='mt-6 text-charcoal-100'>
                    The administrator will see that you declined. They can send you a new invitation later.
                </div>
            </CanonicalModal>
        </div>
    );
}

export default AcceptInvite
